import * as THREE from 'three';
import React, { useEffect, useRef } from 'react';
import { PARTICLE_PLACEMENT, VISUAL_SETTINGS } from '../constants.js';
import { createControls } from './Controls.js';
import {
  clearPostProcessing,
  createPostProcessing,
  resetPostProcessingTrails,
  resizePostProcessing,
} from './PostProcessing.js';
import { createScene } from './Scene.js';
import {
  disposeWindsMode,
  loadWindsMode,
  refreshWindsMode,
  resetWindsMode,
  stepWindsMode,
  updateWindsEnabled,
  updateWindsVisualConfig,
  updateWindsVelocityTexture,
} from './Winds.js';
import { generateGradientRectangle } from './utility/GradientUtil.js';
import { generatePlanetMesh } from './utility/PlanetUtil.js';
import { createStars } from './utility/StarsUtil.js';
import {
  createPressureTexture,
  createVelocityLayerTexture,
  disposeVelocityTexture,
  measureWindMagnitudeRange,
} from './utility/TextureUtil.js';
import { createWebXRExperience } from './xr/WebXRExperience.js';

export default function ThreeScene({
  textureLevel,
  surfaceMode,
  showWinds,
  windMagnitudeLimit,
  windMagnitudeRange,
  windLayerBlendMode,
  legendEntries,
  selectedPoint,
  isXrSurfaceOpaque,
  onPointSelected,
  onTextureLevelStep,
  onSurfaceModeChange,
  onShowWindsChange,
  onXrSurfaceOpaqueChange,
  onWindLayerBlendModeChange,
  onWindMagnitudeRangeChange,
  onVRReady,
  onVRPresentingChange,
  onVRStatusChange,
}) {
  const mountRef = useRef(null);
  const sceneRef = useRef(null);
  const worldGroupRef = useRef(null);
  const cameraRef = useRef(null);
  const rendererRef = useRef(null);
  const controlsRef = useRef(null);
  const composerRef = useRef(null);
  const xrExperienceRef = useRef(null);
  const onPointSelectedRef = useRef(onPointSelected);
  const onTextureLevelStepRef = useRef(onTextureLevelStep);
  const onSurfaceModeChangeRef = useRef(onSurfaceModeChange);
  const onShowWindsChangeRef = useRef(onShowWindsChange);
  const onXrSurfaceOpaqueChangeRef = useRef(onXrSurfaceOpaqueChange);
  const onWindLayerBlendModeChangeRef = useRef(onWindLayerBlendModeChange);
  const onWindMagnitudeRangeChangeRef = useRef(onWindMagnitudeRangeChange);
  const onVRReadyRef = useRef(onVRReady);
  const onVRPresentingChangeRef = useRef(onVRPresentingChange);
  const onVRStatusChangeRef = useRef(onVRStatusChange);
  const showWindsRef = useRef(showWinds);

  const gradientTextureRef = useRef(null);
  const surfaceTextureRef = useRef(null);
  const velocityTextureRef = useRef(null);
  const windResourcesRef = useRef(null);

  useEffect(() => {
    onPointSelectedRef.current = onPointSelected;
  }, [onPointSelected]);

  useEffect(() => {
    onTextureLevelStepRef.current = onTextureLevelStep;
  }, [onTextureLevelStep]);

  useEffect(() => {
    onSurfaceModeChangeRef.current = onSurfaceModeChange;
  }, [onSurfaceModeChange]);

  useEffect(() => {
    onShowWindsChangeRef.current = onShowWindsChange;
  }, [onShowWindsChange]);

  useEffect(() => {
    onXrSurfaceOpaqueChangeRef.current = onXrSurfaceOpaqueChange;
  }, [onXrSurfaceOpaqueChange]);

  useEffect(() => {
    onWindLayerBlendModeChangeRef.current = onWindLayerBlendModeChange;
  }, [onWindLayerBlendModeChange]);

  useEffect(() => {
    onWindMagnitudeRangeChangeRef.current = onWindMagnitudeRangeChange;
  }, [onWindMagnitudeRangeChange]);

  useEffect(() => {
    onVRReadyRef.current = onVRReady;
  }, [onVRReady]);

  useEffect(() => {
    onVRPresentingChangeRef.current = onVRPresentingChange;
  }, [onVRPresentingChange]);

  useEffect(() => {
    onVRStatusChangeRef.current = onVRStatusChange;
  }, [onVRStatusChange]);

  useEffect(() => {
    showWindsRef.current = showWinds;
  }, [showWinds]);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) {
      return undefined;
    }

    const { scene, worldGroup, camera, renderer, resize, setPixelRatioCap } = createScene(mount);
    const controls = createControls(camera, renderer);
    const composer = createPostProcessing(scene, camera, renderer, controls);
    const xrFallbackScene = createXrFallbackScene();
    const xrExperience = createWebXRExperience({
      camera,
      renderer,
      controls,
      worldGroup,
      getPlanetMesh: () => worldGroupRef.current?.getObjectByName('planet'),
      getVelocityTexture: () => velocityTextureRef.current,
      setPixelRatioCap,
      onPointSelected: (payload) => onPointSelectedRef.current?.(payload),
      onTextureLevelStep: (step) => onTextureLevelStepRef.current?.(step),
      onSurfaceModeChange: (mode) => onSurfaceModeChangeRef.current?.(mode),
      onShowWindsChange: (enabled) => onShowWindsChangeRef.current?.(enabled),
      onXrSurfaceOpaqueChange: (enabled) => onXrSurfaceOpaqueChangeRef.current?.(enabled),
      onWindLayerBlendModeChange: (mode) => onWindLayerBlendModeChangeRef.current?.(mode),
      onPresentingChange: (isPresenting) => onVRPresentingChangeRef.current?.(isPresenting),
      onStatusChange: (message) => onVRStatusChangeRef.current?.(message),
    });

    sceneRef.current = scene;
    worldGroupRef.current = worldGroup;
    cameraRef.current = camera;
    rendererRef.current = renderer;
    controlsRef.current = controls;
    composerRef.current = composer;
    xrExperienceRef.current = xrExperience;
    onVRReadyRef.current?.({ enterVR: xrExperience.enterVR });

    createStars(scene);
    generatePlanetMesh({}, worldGroup);

    const handleResize = () => {
      resize();
      if (!renderer.xr.isPresenting) {
        resizePostProcessing(composer, mount.clientWidth, mount.clientHeight);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    const clock = new THREE.Clock();
    let lastXrFrameErrorAt = 0;

    const animate = (time, xrFrame) => {
      void time;
      const delta = Math.min(clock.getDelta(), VISUAL_SETTINGS.maxDeltaSeconds);

      if (renderer.xr.isPresenting) {
        try {
          xrExperience.beforeFrame(delta, xrFrame);
          renderer.autoClear = true;
          renderer.clear(true, true, true);
          renderer.render(xrExperience.getScene(), camera);
        } catch (error) {
          const now = performance.now();
          const errorMessage = formatRuntimeError(error);

          if ((now - lastXrFrameErrorAt) > 1000) {
            lastXrFrameErrorAt = now;
            onVRStatusChangeRef.current?.(`XR frame fallback: ${errorMessage}`);
          }

          if (typeof window !== 'undefined') {
            window.__OASIS_XR_FRAME_ERROR__ = {
              message: errorMessage,
              stack: error?.stack ?? null,
              at: new Date().toISOString(),
            };
          }

          xrFallbackScene.userData.setMessage?.(errorMessage);
          renderer.autoClear = true;
          renderer.clear(true, true, true);
          renderer.render(xrFallbackScene, camera);
        }
        return;
      }

      controls.update();
      xrExperience.beforeFrame(delta, xrFrame);

      const windResources = windResourcesRef.current;
      if (windResources?.enabled) {
        if (windResources.needsImmediateReset) {
          resetWindsMode(windResources, camera, renderer);
          windResources.needsImmediateReset = false;
        } else if (
          windResources.needsRespawn &&
          (!windResources.interacting || ((performance.now() - windResources.lastRespawnAt) >= VISUAL_SETTINGS.respawnThrottleMs))
        ) {
          refreshWindsMode(windResources, camera);
        }

        stepWindsMode(windResources, renderer, delta);
      }

      composer.render();
    };

    renderer.setAnimationLoop(animate);

    return () => {
      renderer.setAnimationLoop(null);

      window.removeEventListener('resize', handleResize);
      onVRReadyRef.current?.(null);
      onVRPresentingChangeRef.current?.(false);
      xrExperience.dispose();
      xrExperienceRef.current = null;

      disposeWindsMode(windResourcesRef.current, controls);
      windResourcesRef.current = null;

      disposeVelocityTextureRef(velocityTextureRef);
      disposeTextureRef(gradientTextureRef);
      disposeTextureRef(surfaceTextureRef);

      disposeSceneObject(worldGroup.getObjectByName('planet'));
      disposeSceneObject(scene.getObjectByName('stars'));
      disposeSceneObject(xrFallbackScene);

      clearPostProcessing(composerRef);
      controls.dispose();
      renderer.dispose();

      if (renderer.domElement.parentNode === mount) {
        mount.removeChild(renderer.domElement);
      }
    };
  }, []);

  useEffect(() => {
    xrExperienceRef.current?.updateState({
      textureLevel,
      surfaceMode,
      showWinds,
      windMagnitudeLimit,
      windMagnitudeRange,
      windLayerBlendMode,
      legendEntries,
      selectedPoint,
      surfaceOpaque: isXrSurfaceOpaque,
    });
  }, [
    textureLevel,
    surfaceMode,
    showWinds,
    windMagnitudeLimit,
    windMagnitudeRange,
    windLayerBlendMode,
    legendEntries,
    selectedPoint,
    isXrSurfaceOpaque,
  ]);

  useEffect(() => {
    let cancelled = false;

    async function syncSurfaceState() {
      if (!worldGroupRef.current) {
        return;
      }

      let nextSurfaceTexture = null;

      if (surfaceMode === 'pressure') {
        nextSurfaceTexture = await createPressureTexture(textureLevel);
      }

      if (cancelled) {
        nextSurfaceTexture?.dispose();
        return;
      }

      const previousSurfaceTexture = surfaceTextureRef.current;
      surfaceTextureRef.current = nextSurfaceTexture;

      generatePlanetMesh({ texture: nextSurfaceTexture }, worldGroupRef.current);

      if (previousSurfaceTexture && previousSurfaceTexture !== nextSurfaceTexture) {
        previousSurfaceTexture.dispose();
      }
    }

    syncSurfaceState();

    return () => {
      cancelled = true;
    };
  }, [surfaceMode, textureLevel]);

  useEffect(() => {
    let cancelled = false;

    async function syncWindsState() {
      if (!worldGroupRef.current || !cameraRef.current || !rendererRef.current || !controlsRef.current) {
        return;
      }

      if (!gradientTextureRef.current) {
        gradientTextureRef.current = generateGradientRectangle();
      }

      const nextVelocityTexture = await createVelocityLayerTexture(textureLevel, windLayerBlendMode);
      if (cancelled) {
        disposeVelocityTexture(nextVelocityTexture);
        return;
      }

      const previousVelocityTexture = velocityTextureRef.current;
      velocityTextureRef.current = nextVelocityTexture;
      const nextWindMagnitudeRange = measureWindMagnitudeRange(nextVelocityTexture);
      onWindMagnitudeRangeChangeRef.current?.({
        level: textureLevel,
        layerBlendMode: windLayerBlendMode,
        range: nextWindMagnitudeRange,
      });

      const planetMesh = worldGroupRef.current.getObjectByName('planet') ?? generatePlanetMesh({}, worldGroupRef.current);

      if (!windResourcesRef.current) {
        windResourcesRef.current = loadWindsMode({
          scene: worldGroupRef.current,
          camera: cameraRef.current,
          renderer: rendererRef.current,
          controls: controlsRef.current,
          velocityTexture: nextVelocityTexture,
          enabled: showWindsRef.current,
          windMagnitudeLimit: nextWindMagnitudeRange.max,
          windMagnitudeRange: nextWindMagnitudeRange,
          gradientTexture: gradientTextureRef.current,
          planetMesh,
          onPointSelected: (payload) => onPointSelectedRef.current?.(payload),
          particleResolution: rendererRef.current.xr.isPresenting
            ? VISUAL_SETTINGS.webxrQuest.particleResolution
            : VISUAL_SETTINGS.particleResolution,
          particlePlacement: rendererRef.current.xr.isPresenting
            ? PARTICLE_PLACEMENT.fullSphere
            : PARTICLE_PLACEMENT.visibleCap,
        });
      } else {
        updateWindsVisualConfig(windResourcesRef.current, {
          maxMagnitude: nextWindMagnitudeRange.max,
          windMagnitudeRange: nextWindMagnitudeRange,
        });
        updateWindsVelocityTexture(windResourcesRef.current, nextVelocityTexture);
        resetWindsMode(windResourcesRef.current, cameraRef.current, rendererRef.current);
        resetPostProcessingTrails(composerRef.current);
      }

      if (previousVelocityTexture && previousVelocityTexture !== nextVelocityTexture) {
        disposeVelocityTexture(previousVelocityTexture);
      }
    }

    syncWindsState();

    return () => {
      cancelled = true;
    };
  }, [textureLevel, windLayerBlendMode]);

  useEffect(() => {
    if (!windResourcesRef.current) {
      return;
    }

    updateWindsVisualConfig(windResourcesRef.current, {
      maxMagnitude: windMagnitudeLimit,
      windMagnitudeRange,
    });
  }, [windMagnitudeLimit, windMagnitudeRange]);

  useEffect(() => {
    if (!windResourcesRef.current || !cameraRef.current || !rendererRef.current) {
      return;
    }

    updateWindsEnabled(
      windResourcesRef.current,
      showWinds,
      cameraRef.current,
      rendererRef.current
    );
    resetPostProcessingTrails(composerRef.current);
  }, [showWinds]);

  return <div ref={mountRef} style={{ width: '100%', height: '100vh' }} />;
}

function disposeTextureRef(textureRef) {
  textureRef.current?.dispose();
  textureRef.current = null;
}

function disposeVelocityTextureRef(textureRef) {
  disposeVelocityTexture(textureRef.current);
  textureRef.current = null;
}

function disposeSceneObject(object) {
  if (!object) {
    return;
  }

  object.parent?.remove(object);

  object.traverse((child) => {
    child.geometry?.dispose();

    if (Array.isArray(child.material)) {
      child.material.forEach((material) => {
        material.map?.dispose();
        material.dispose();
      });
      return;
    }

    child.material?.map?.dispose();
    child.material?.dispose();
  });
}

function createXrFallbackScene() {
  const fallbackScene = new THREE.Scene();
  fallbackScene.background = new THREE.Color(0x081524);

  const group = new THREE.Group();
  group.name = 'xr-fallback-marker';

  const marker = new THREE.Mesh(
    new THREE.SphereGeometry(0.18, 32, 16),
    new THREE.MeshBasicMaterial({ color: 0x7ad4ff })
  );
  marker.position.set(0, 0, -1.7);
  group.add(marker);

  const ring = new THREE.Mesh(
    new THREE.RingGeometry(0.28, 0.31, 48),
    new THREE.MeshBasicMaterial({
      color: 0xffffff,
      transparent: true,
      opacity: 0.74,
      side: THREE.DoubleSide,
    })
  );
  ring.position.set(0, 0, -1.72);
  group.add(ring);

  const label = new THREE.Mesh(
    new THREE.PlaneGeometry(1.24, 0.42),
    new THREE.MeshBasicMaterial({
      map: createFallbackLabelTexture('waiting for error details'),
      transparent: true,
      side: THREE.DoubleSide,
      depthTest: false,
      depthWrite: false,
    })
  );
  label.position.set(0, -0.42, -1.68);
  label.renderOrder = 1000;
  group.add(label);

  fallbackScene.userData.setMessage = (message) => {
    const previousMap = label.material.map;
    label.material.map = createFallbackLabelTexture(message);
    label.material.needsUpdate = true;
    previousMap?.dispose();
  };

  fallbackScene.add(group);
  return fallbackScene;
}

function formatRuntimeError(error) {
  const name = error?.name ? `${error.name}: ` : '';
  return `${name}${error?.message ?? 'Unknown render error'}`;
}

function createFallbackLabelTexture(message) {
  const canvas = document.createElement('canvas');
  canvas.width = 1024;
  canvas.height = 360;
  const context = canvas.getContext('2d');
  const [lineA, lineB] = splitFallbackMessage(message);

  context.clearRect(0, 0, canvas.width, canvas.height);
  drawFallbackRoundedRect(
    context,
    10,
    10,
    1004,
    340,
    34,
    'rgba(3, 9, 19, 0.82)',
    'rgba(255, 255, 255, 0.34)'
  );
  context.fillStyle = '#ffffff';
  context.font = '700 58px "Segoe UI", sans-serif';
  context.textAlign = 'center';
  context.textBaseline = 'middle';
  context.fillText('XR fallback', 512, 82);
  context.fillStyle = '#7ad4ff';
  context.font = '600 38px "Segoe UI", sans-serif';
  context.fillText('app frame error', 512, 142);
  context.fillStyle = '#ecf4fb';
  context.font = '600 32px "Segoe UI", sans-serif';
  context.fillText(lineA, 512, 218);
  context.fillText(lineB, 512, 268);

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.needsUpdate = true;
  return texture;
}

function splitFallbackMessage(message) {
  const normalised = String(message || 'Unknown render error')
    .replace(/\s+/g, ' ')
    .trim();
  const maxLength = 42;

  if (normalised.length <= maxLength) {
    return [normalised, ''];
  }

  const firstBreak = normalised.lastIndexOf(' ', maxLength);
  const splitIndex = firstBreak > 16 ? firstBreak : maxLength;
  const lineA = normalised.slice(0, splitIndex).trim();
  const lineB = normalised.slice(splitIndex).trim();

  return [
    lineA,
    lineB.length > maxLength ? `${lineB.slice(0, maxLength - 1).trim()}...` : lineB,
  ];
}

function drawFallbackRoundedRect(context, x, y, width, height, radius, fillStyle, strokeStyle) {
  context.beginPath();
  context.moveTo(x + radius, y);
  context.arcTo(x + width, y, x + width, y + height, radius);
  context.arcTo(x + width, y + height, x, y + height, radius);
  context.arcTo(x, y + height, x, y, radius);
  context.arcTo(x, y, x + width, y, radius);
  context.closePath();
  context.fillStyle = fillStyle;
  context.fill();

  if (strokeStyle) {
    context.strokeStyle = strokeStyle;
    context.lineWidth = 4;
    context.stroke();
  }
}
